import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { PhoneIcon } from './Icons';
import api from '../api';

export default function IncomingCallModal() {
  const { socket, token } = useAuth();
  const navigate = useNavigate();
  const [call, setCall] = useState(null); // { call_id, room_id, caller_id, caller_nickname }
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!socket) return;
    const handleIncoming = (data) => {
      setCall(data);
      // Auto-dismiss after 30s
      timerRef.current = setTimeout(() => setCall(null), 30000);
    };
    socket.on('incoming_call', handleIncoming);
    return () => { socket.off('incoming_call', handleIncoming); };
  }, [socket]);

  // Play ringtone effect via vibration
  useEffect(() => {
    if (!call) return;
    // Try vibrate on mobile
    if (navigator.vibrate) {
      const interval = setInterval(() => navigator.vibrate([300, 200, 300]), 1000);
      return () => { clearInterval(interval); navigator.vibrate(0); };
    }
  }, [call]);

  const handleAccept = async () => {
    if (!call) return;
    clearTimeout(timerRef.current);
    socket?.emit('call_accepted', { call_id: call.call_id, caller_id: call.caller_id });
    try {
      const voiceData = await api.voiceJoin(call.room_id, token);
      navigate(`/chat/${call.room_id}`, {
        state: {
          voiceState: {
            callId: voiceData.call_id,
            livekitRoom: voiceData.livekit_room,
            livekitToken: voiceData.livekit_token,
            livekitUrl: voiceData.livekit_url,
          }
        }
      });
    } catch {
      navigate(`/chat/${call.room_id}`);
    }
    setCall(null);
  };

  const handleReject = () => {
    if (!call) return;
    clearTimeout(timerRef.current);
    socket?.emit('call_rejected', { call_id: call.call_id, caller_id: call.caller_id });
    setCall(null);
  };

  if (!call) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl p-8 w-full max-w-xs text-center space-y-6 animate-pulse-slow">
        {/* Caller info */}
        <div className="space-y-2">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-3xl font-bold text-white animate-bounce">
            <PhoneIcon size={36} />
          </div>
          <h2 className="text-xl font-bold text-white">{call.caller_nickname || '상대방'}</h2>
          <p className="text-white/50 text-sm">음성 통화 요청</p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-8">
          <button onClick={handleReject}
            className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition active:scale-95 shadow-lg shadow-red-500/30">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
              <line x1="23" y1="1" x2="1" y2="23"/>
            </svg>
          </button>
          <button onClick={handleAccept}
            className="w-16 h-16 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition active:scale-95 shadow-lg shadow-green-500/30 animate-pulse">
            <PhoneIcon size={28} />
          </button>
        </div>
        <div className="flex justify-center gap-12 text-xs text-white/40">
          <span>거절</span>
          <span>수락</span>
        </div>
      </div>
    </div>
  );
}
